/* ============================================================================
   GMR SEARCH — self-contained search-results enhancer (V4).

   WHY ITS OWN FILE: the search redesign kept getting CLOBBERED when parallel
   sessions pushed a stale assets/gmr.js (all features used to live there). This
   module lives in its OWN file + reads its OWN data element (#gmr-search-data,
   emitted by sections/gmr-search.liquid) + injects its OWN CSS, so it depends on
   ZERO shared files (not gmr.js, not gmr.css, not the card snippet). A gmr.js
   clobber can't touch it. (The old gmr.js initSmartSearch now finds no
   #gmr-search-pool and harmlessly bails.)

   WHAT IT DOES: reads the natural-language query intent, RANKS the one results
   grid best-first, stamps a single AI "Perfect/Best match" ribbon on the closest
   rug, and adds honest Bestseller/Trending tags (from gmr-popularity.json, real
   order data). Re-runs after each Loom Bar / pager AJAX swap via a MutationObserver
   on #plp-swap — no hook into gmr.js needed.
   ============================================================================ */
(function () {
  'use strict';

  /* ---- tiny self-contained helpers (no gmr.js dependency) ---- */
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---- week seed: equal-score results rotate weekly (changes each Monday, UTC;
          higher scores always stay ahead). ?gmrweek=N forces a seed for testing. ---- */
  var WK = (function () { var m = location.search.match(/[?&]gmrweek=(\d+)/); return m ? parseInt(m[1], 10) : Math.floor((Math.floor(Date.now() / 864e5) + 3) / 7); })();
  function wkHash(s) { var h = 2166136261 ^ WK; s = String(s); for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }

  /* ---- one-time CSS injection (so a gmr.css clobber can't unstyle us) ---- */
  function injectCss() {
    if (document.getElementById('gmr-search-css')) return;
    var css = '' +
      '.pc__best{position:absolute;left:10px;bottom:10px;z-index:2;white-space:nowrap;display:inline-flex;align-items:center;gap:5px;font:700 10.5px var(--font-ui,system-ui,sans-serif);letter-spacing:.04em;color:#fff;background:var(--clay,#b0613c);border-radius:999px;padding:4px 11px 4px 8px;box-shadow:0 3px 11px -3px rgba(176,97,60,.7);pointer-events:none}' +
      '.pc__best__ai{width:13px;height:13px;fill:currentColor;flex:none}' +
      '.pc__best b{font-weight:800;letter-spacing:.09em}' +
      '.pc__pop{position:absolute;right:10px;bottom:10px;z-index:2;white-space:nowrap;font:700 10px var(--font-ui,system-ui,sans-serif);letter-spacing:.06em;text-transform:uppercase;padding:4px 9px;border-radius:3px;pointer-events:none}' +
      '.pc__pop[hidden]{display:none!important}' +
      '.pc__pop--best{background:var(--ink,#2d241e);color:#F3EDE0}' +
      '.pc__pop--trending{background:rgba(247,242,233,.94);color:var(--clay-deep,#8a4a2c);box-shadow:0 1px 5px rgba(45,36,30,.16)}' +
      '@media(max-width:749px){.pc__best{left:7px;bottom:7px;padding:3px 9px;font-size:10px}.pc__pop{right:7px;bottom:7px;padding:3px 7px;font-size:9px}}';
    var s = document.createElement('style');
    s.id = 'gmr-search-css';
    s.textContent = css;
    document.head.appendChild(s);
  }

  /* ---- lexicons (hardcoded brain — same scoring as before) ---- */
  var COLORS = {
    blue: ['blue', 'navy', 'indigo', 'teal', 'turquoise', 'denim', 'cobalt', 'sky', 'aqua', 'peacock'],
    green: ['green', 'sage', 'olive', 'emerald', 'mint', 'forest', 'moss', 'fern', 'pistachio'],
    red: ['red', 'crimson', 'burgundy', 'maroon', 'wine', 'scarlet', 'brick'],
    grey: ['grey', 'gray', 'silver', 'charcoal', 'slate', 'ash', 'pewter', 'smoke'],
    beige: ['beige', 'cream', 'ivory', 'tan', 'sand', 'taupe', 'natural', 'neutral', 'oatmeal', 'greige', 'wheat'],
    brown: ['brown', 'chocolate', 'coffee', 'mocha', 'walnut', 'espresso', 'camel', 'caramel'],
    black: ['black', 'onyx', 'ebony'],
    white: ['white', 'snow'],
    pink: ['pink', 'blush', 'rose', 'salmon', 'mauve'],
    purple: ['purple', 'lavender', 'lilac', 'plum', 'violet', 'eggplant'],
    orange: ['orange', 'rust', 'terracotta', 'copper', 'amber', 'apricot'],
    gold: ['gold', 'yellow', 'mustard', 'ochre', 'saffron', 'honey'],
    multi: ['multi', 'multicolor', 'multicolour', 'colorful', 'colourful', 'rainbow']
  };
  var ROOMS = {
    bedroom: { tokens: ['bedroom', 'master', 'guest room'], sizes: ['5x8', '5x7', '6x9'] },
    dining: { tokens: ['dining'], sizes: ['9x12', '8x10'] },
    kitchen: { tokens: ['kitchen'], sizes: ['runner', '2x', '26x', '2.6x', '25x'] },
    entry: { tokens: ['entry', 'entryway', 'foyer', 'hallway', 'hall', 'corridor', 'passage', 'mudroom'], sizes: ['runner', '2x', '26x', '2.6x', '25x'] },
    office: { tokens: ['office', 'study'], sizes: ['5x8', '6x9', '5x7'] },
    accent: { tokens: ['accent', 'doormat', 'bathroom', ' bath', 'nursery', 'kids', 'children', 'small'], sizes: ['3x5', '2x3', '4x6', '2x4'] },
    living: { tokens: ['living', 'lounge', 'family room', 'drawing room', 'great room', 'sitting room'], sizes: ['8x10', '9x12'] }
  };
  var SHAPES = { round: ['round', 'circle', 'circular'], runner: ['runner'], square: ['square'], oval: ['oval'], octagon: ['octagon', 'octagonal'] };
  var MATERIALS = {
    wool: ['wool'], silk: ['silk'], jute: ['jute', 'sisal', 'natural fibre', 'natural fiber'],
    cotton: ['cotton'], viscose: ['viscose', 'art silk'],
    'hand-knotted': ['hand knotted', 'hand-knotted', 'handknotted'],
    'hand-tufted': ['hand tufted', 'hand-tufted', 'tufted'],
    flatweave: ['flatweave', 'flat weave', 'kilim', 'dhurrie'], shag: ['shag', 'shaggy'],
    leather: ['leather'], hemp: ['hemp']
  };
  var PATTERNS = {
    oriental: ['oriental', 'persian'], abstract: ['abstract'], geometric: ['geometric'],
    floral: ['floral'], tribal: ['tribal'], moroccan: ['moroccan', 'beni ourain', 'beni'],
    bohemian: ['boho', 'bohemian'], vintage: ['vintage', 'distressed', 'antique'],
    traditional: ['traditional'], modern: ['modern', 'contemporary'],
    southwestern: ['southwestern', 'aztec'], medallion: ['medallion'], striped: ['striped', 'stripe']
  };

  var reCache = {};
  function escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function reFor(list) {
    var key = list.join('|');
    if (!reCache[key]) reCache[key] = new RegExp('\\b(' + list.map(escRe).join('|') + ')\\b');
    return reCache[key];
  }
  function hit(hay, list) { return reFor(list).test(hay); }
  function firstKey(map, hay) { for (var k in map) { var l = map[k].tokens || map[k]; if (hit(hay, l)) return k; } return null; }
  function sizeHit(sizeStr, targets) {
    if (!sizeStr) return false;
    var toks = sizeStr.split('|');
    for (var i = 0; i < toks.length; i++) { if (!toks[i]) continue; for (var j = 0; j < targets.length; j++) { if (toks[i].indexOf(targets[j]) > -1) return true; } }
    return false;
  }

  /* ---- RANK the grid best-first + AI ribbon on the closest rug ---- */
  function rankAndRibbon(grid) {
    var poolEl = document.getElementById('gmr-search-data');
    if (!poolEl) return null;
    var terms = (poolEl.getAttribute('data-terms') || '').toLowerCase();
    if (!terms) return null;
    var pool;
    try { pool = JSON.parse(poolEl.textContent); } catch (e) { return null; }
    if (!pool || !pool.length) return null;

    var sizeMatch = terms.match(/(\d{1,2}(?:\.\d)?)['′"\s]*(?:x|×|by)['′"\s]*(\d{1,2}(?:\.\d)?)/);
    var sizeTok = sizeMatch ? (sizeMatch[1] + 'x' + sizeMatch[2]) : null;
    // Design/SKU-shaped query (N00816 or full SKU N00816K0000A9G) — matched against the pool's
    // "d" design code (handle suffix, which is also the SKU prefix). Dominates all other dims.
    var codeQ = terms.replace(/\s+/g, '');
    if (!/^[a-z]{1,2}\d{3,}[a-z0-9]*$/.test(codeQ)) codeQ = null;
    var intent = {
      code: codeQ,
      color: firstKey(COLORS, terms), size: sizeTok,
      room: sizeTok ? null : firstKey(ROOMS, terms),
      shape: firstKey(SHAPES, terms), material: firstKey(MATERIALS, terms), pattern: firstKey(PATTERNS, terms)
    };
    var dims = ['code', 'color', 'size', 'room', 'shape', 'material', 'pattern'].filter(function (d) { return intent[d]; });
    if (!dims.length) return null;

    var W = { code: 10, color: 3, size: 3, room: 3, shape: 2, material: 2, pattern: 2 };
    pool.forEach(function (it) {
      var sc = 0, m = {};
      if (intent.code && it.d) {
        var dc = it.d.toLowerCase();
        if (dc && (intent.code === dc || intent.code.indexOf(dc) === 0 || dc.indexOf(intent.code) === 0)) { sc += W.code; m.code = 1; }
      }
      if (intent.color && hit(it.hay, COLORS[intent.color])) { sc += W.color; m.color = 1; }
      if (intent.size && sizeHit(it.s, [intent.size])) { sc += W.size; m.size = 1; }
      if (intent.room && sizeHit(it.s, ROOMS[intent.room].sizes)) { sc += W.room; m.room = 1; }
      if (intent.shape && sizeHit(it.sh, SHAPES[intent.shape])) { sc += W.shape; m.shape = 1; }
      if (intent.material && hit(it.hay, MATERIALS[intent.material])) { sc += W.material; m.material = 1; }
      if (intent.pattern && hit(it.hay, PATTERNS[intent.pattern])) { sc += W.pattern; m.pattern = 1; }
      it._sc = sc;
      it._full = dims.every(function (d) { return m[d]; });
    });

    var byUrl = {};
    pool.forEach(function (it) { byUrl[it.u] = it; });
    var cards = $all('.pc', grid);
    if (!cards.length) return null;
    cards.forEach(function (c) {
      var it = byUrl[c.getAttribute('href')];
      c._sc = it ? it._sc : 0;
      c._full = it ? !!it._full : false;
    });
    var sorted = cards.slice().sort(function (a, b) { return b._sc - a._sc || wkHash(a.getAttribute('href')) - wkHash(b.getAttribute('href')); });
    sorted.forEach(function (c) { grid.appendChild(c); });

    var best = sorted[0];
    if (best && best._sc > 0) {
      var img = best.querySelector('.pc__img');
      if (img && !img.querySelector('.pc__best')) {
        var ribbon = document.createElement('span');
        ribbon.className = 'pc__best';
        var label = (dims.length >= 2 && best._full) ? 'Perfect match' : 'Best match';
        ribbon.innerHTML = '<svg class="pc__best__ai" viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
          + '<path d="M12 2.2 13.7 7.1 18.6 8.8 13.7 10.5 12 15.4 10.3 10.5 5.4 8.8 10.3 7.1Z"/>'
          + '<path d="M18.6 13.6 19.45 15.95 21.8 16.8 19.45 17.65 18.6 20 17.75 17.65 15.4 16.8 17.75 15.95Z"/>'
          + '</svg><b>AI</b>' + label;
        img.appendChild(ribbon);
      }
      return best; // the hero card, so popularity tagging can skip it
    }
    return null;
  }

  /* ---- honest popularity tags (Bestseller > Trending) from real order data ---- */
  var _pop = null, _popFetching = false;
  function cardPid(card) { var el = card.querySelector('[data-pid]'); return el ? String(el.getAttribute('data-pid')) : ''; }
  function popEl(card) {
    var el = card.querySelector('.pc__pop');
    if (el) return el;
    var img = card.querySelector('.pc__img');
    if (!img) return null;
    el = document.createElement('span');
    el.className = 'pc__pop';
    el.hidden = true;
    img.appendChild(el);
    return el;
  }
  function applyPop(grid, hero) {
    if (!_pop) return;
    $all('.pc', grid).forEach(function (card) {
      var el = popEl(card);
      if (!el) return;
      if (card === hero) { el.hidden = true; return; }      // hero shows the AI ribbon, not a tag
      var id = cardPid(card);
      if (_pop.best[id]) { el.textContent = 'Bestseller'; el.className = 'pc__pop pc__pop--best'; el.hidden = false; }
      else if (_pop.trend[id]) { el.textContent = 'Trending'; el.className = 'pc__pop pc__pop--trending'; el.hidden = false; }
      else { el.hidden = true; }
    });
  }
  function loadPop(grid, hero) {
    if (_pop) { applyPop(grid, hero); return; }
    if (_popFetching) return;
    var dataEl = document.getElementById('gmr-search-data');
    var src = dataEl && dataEl.getAttribute('data-pop-src');
    if (!src) return;
    _popFetching = true;
    fetch(src).then(function (r) { return r.json(); }).then(function (d) {
      _pop = { best: {}, trend: {} };
      ((d && d.bestseller) || []).forEach(function (id) { _pop.best[String(id)] = 1; });
      ((d && d.trending) || []).forEach(function (id) { _pop.trend[String(id)] = 1; });
      applyPop(grid, hero);
    }).catch(function () {});
  }

  /* ---- run + self-wire ---- */
  function run() {
    var swap = document.getElementById('plp-swap');
    if (!swap) return;
    var grid = swap.querySelector('.grid');
    if (!grid) return;
    injectCss();
    var hero = rankAndRibbon(grid);
    loadPop(grid, hero);
  }

  var raf = 0;
  function schedule() { if (raf) return; raf = requestAnimationFrame(function () { raf = 0; run(); }); }

  function init() {
    run();
    var swap = document.getElementById('plp-swap');
    if (swap && !swap._gmrSearchObs) {
      // Loom Bar / pager AJAX replaces #plp-swap's innerHTML → re-rank+tag the fresh grid.
      // childList on #plp-swap fires on the swap; our own reorder mutates .grid (a descendant),
      // so subtree:false avoids a feedback loop.
      new MutationObserver(schedule).observe(swap, { childList: true, subtree: false });
      swap._gmrSearchObs = 1;
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
